/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import type {EagColumnState} from '../../types/EagColumnState';
import type {Filters} from '../../types/Filters';
import type {RowData} from '../../types/RowData';
import type {ToolpanelType} from '../../types/ToolpanelType';
import {
  selectActivePanel,
  selectBulkActions,
  selectHasBulkActions,
  selectAllFilteredSelected,
  selectColumnStates,
  selectFetchState,
  selectFilterValues,
  selectHasFetchError,
  selectHasCurrentTotalCount,
  selectIsLoading,
  selectPage,
  selectPreviousRows,
  selectRows,
  selectSelectedRowIds,
  selectTotalCount,
  type FetchState,
} from '../createEagGridStore';
import {useEagGridSelector} from '../useEagGridSelector';

/**
 * Consolidated trivial selector hooks.
 *
 * Each is a pure pass-through `useEagGridSelector(selectX)` with no memoization
 * or stability guard — grouped here (mirroring useBehavior.ts) so the selectors/
 * folder is not cluttered with one 6-line file per primitive. Memoized/derived
 * selectors (columns, ordered columns, cell-resize) keep their own files.
 */

export function useGridRows(): RowData[] {
  return useEagGridSelector(selectRows);
}

export const useGridBulkActions = () => useEagGridSelector(selectBulkActions);
export const useGridHasBulkActions = () => useEagGridSelector(selectHasBulkActions);

export function useGridTotalCount(): number {
  return useEagGridSelector(selectTotalCount);
}

export function useGridHasCurrentTotalCount(): boolean {
  return useEagGridSelector(selectHasCurrentTotalCount);
}

export function useGridIsLoading(): boolean {
  return useEagGridSelector(selectIsLoading);
}

export function useGridFetchState(): FetchState {
  return useEagGridSelector(selectFetchState);
}

export function useGridHasFetchError(): boolean {
  return useEagGridSelector(selectHasFetchError);
}

export function useGridPage(): number {
  return useEagGridSelector(selectPage);
}

export function useGridPreviousRows(): RowData[] {
  return useEagGridSelector(selectPreviousRows);
}

export function useActivePanel(): ToolpanelType | null {
  return useEagGridSelector(selectActivePanel);
}

export function useFilterValues(): Filters {
  return useEagGridSelector(selectFilterValues);
}

export function useColumnStates(): EagColumnState[] {
  return useEagGridSelector(selectColumnStates);
}

export function useSelectedRowIds(): Set<string> {
  return useEagGridSelector(selectSelectedRowIds);
}

export const useAllFilteredSelected = () => useEagGridSelector(selectAllFilteredSelected);
